import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import type { Doc, Id } from "@convex/_generated/dataModel";

export interface AllowlistManagerProps {
  entries: Doc<"emailAllowlist">[];
  onAdd: (args: { pattern: string; type: "address" | "domain" }) => Promise<{ entryId: Id<"emailAllowlist">; warning?: string | null }>;
  onRemove: (args: { entryId: Id<"emailAllowlist"> }) => Promise<void>;
}

export function AllowlistManager({ entries, onAdd, onRemove }: AllowlistManagerProps) {
  const [pattern, setPattern] = useState("");
  const [type, setType] = useState<"address" | "domain">("address");
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  async function handleAdd() {
    if (!pattern.trim()) return;
    setAdding(true);
    setWarning(null);
    setError(null);
    try {
      const result = await onAdd({ pattern: pattern.trim(), type });
      if (result.warning) {
        setWarning(result.warning);
      }
      setPattern("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add entry");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-black dark:text-white">Allowed Senders</h4>
      <p className="text-xs text-black/50 dark:text-white/50">
        Only emails from allowed addresses or domains reach your agent.
      </p>

      {/* Add form */}
      <div className="flex items-center gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as "address" | "domain")}
          className="rounded-lg border border-black/10 bg-black/5 px-2 py-1.5 text-xs text-black dark:border-white/10 dark:bg-white/5 dark:text-white"
        >
          <option value="address">Address</option>
          <option value="domain">Domain</option>
        </select>
        <input
          type="text"
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={type === "address" ? "user@example.com" : "example.com"}
          className="min-w-0 flex-1 rounded-lg border border-black/10 bg-black/5 px-3 py-1.5 text-xs text-black placeholder:text-black/30 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/30"
        />
        <button
          onClick={handleAdd}
          disabled={adding || !pattern.trim()}
          className="flex items-center gap-1 rounded-lg bg-brand-coral px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-coral/90 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          Add
        </button>
      </div>

      {/* Warning banner (FMEA #4 — domain wildcard warning) */}
      {warning && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      {/* Entries list */}
      {entries.length === 0 ? (
        <p className="py-2 text-xs text-black/30 dark:text-white/30">No entries yet.</p>
      ) : (
        <ul className="space-y-1">
          {entries.map((entry) => (
            <li
              key={entry._id}
              className="flex items-center justify-between rounded-lg border border-black/5 px-3 py-2 dark:border-white/5"
            >
              <div className="min-w-0 flex-1">
                <span className="block truncate text-xs font-mono text-black dark:text-white">
                  {entry.type === "domain" ? `@${entry.pattern}` : entry.pattern}
                </span>
                <span className="text-[10px] text-black/40 dark:text-white/40">
                  {entry.type === "domain" ? "Domain" : "Address"}
                </span>
              </div>
              <button
                onClick={() => onRemove({ entryId: entry._id })}
                className="ml-2 rounded p-1 text-black/30 transition-colors hover:bg-red-500/10 hover:text-red-500 dark:text-white/30"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
